<template lang="pug">
button.wallet-container.from-header-field-from.to-header-field-to.bg-linear-to-b.border-header-field-border(type="button", :class="{ 'border-header-field-border-highlight': activityWalletEnable }", :disabled="props.disabled", @click="click")
  .wallet-container.inner(class="border-header-field-border/40")
    .wallet-wrapper.flex.items-center.text-white.rounded-full(class="gap-0.5")
      .wallet-content.flex-1.flex.flex-col.items-center.justify-between(class="gap-x-1.5 md:flex-row")
        .wallet-content-title.flex.flex-row.items-center.justify-center
          img.wallet-content-mode-icon(:src="getModeIconImage")
          span.wallet-content-label.text-2xs.leading-none(:class="{ 'text-header-field-border-highlight': activityWalletEnable }") {{ modeLabel }}
        span.wallet-content-amount.text-xs.leading-none {{ amount }}
      .relative
        img.wallet-icon(:src="props.disabled ? getWalletDisableImage : getWalletNormalImage")
        img.checked-icon(v-if="!props.disabled && mode === 'activity' && checked", src="assets/images/vip_card/vcard_icon_check_normal.png")
</template>

<script setup>
import { computed } from 'vue'
import { useNuxtApp } from '#app'
import { useI18n } from '#imports'
import walletBtnActivityDisable from 'assets/images/wallet/wallet_btn_activity_disable.png'
import walletBtnActivityNormal from 'assets/images/wallet/wallet_btn_activity_normal.png'
import walletBtnCashDisable from 'assets/images/wallet/wallet_btn_cash_disable.png'
import walletBtnCashNormal from 'assets/images/wallet/wallet_btn_cash_normal.png'
import walletBtnPointDisable from 'assets/images/wallet/wallet_btn_point_disable.png'
import walletBtnPointNormal from 'assets/images/wallet/wallet_btn_point_normal.png'
import topmenuIconCoinNormal from 'assets/images/common/topmenu_icon_coin_normal.png'
import topmenuIconFreecreditNormal from 'assets/images/common/topmenu_icon_freecredit_normal.png'
import topmenuIconDiamondNormal from 'assets/images/common/topmenu_icon_diamond_normal.png'

// Nuxt App
const { $SoundControl } = useNuxtApp()
const { t } = useI18n()

// Props
const props = defineProps({
  mode: {
    type: String,
    validator(value) {
      return ['cash', 'activity', 'point'].includes(value)
    },
    default: 'cash',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  amount: {
    type: String,
    required: true,
  },
  checked: {
    type: Boolean,
    default: false,
  },
})

// Emits
const emit = defineEmits(['click'])

// Computed properties
const modeIcon = computed(() => {
  const iconName = props.mode === 'cash' ? 'coin' : props.mode === 'activity' ? 'freecredit' : 'diamond'
  return iconName
})

const modeLabel = computed(() => {
  switch (props.mode) {
    case 'cash':
      return t('payment.cash')
    case 'activity':
      return t('payment.activity')
    case 'point':
      return t('common.point')
    default:
      return ''
  }
})

const activityWalletEnable = computed(() => {
  return props.mode === 'activity' && !props.disabled
})

const getModeIconImage = computed(() => {
  const iconMap = {
    coin: topmenuIconCoinNormal,
    freecredit: topmenuIconFreecreditNormal,
    diamond: topmenuIconDiamondNormal,
  }
  return iconMap[modeIcon.value] || topmenuIconCoinNormal
})

const getWalletDisableImage = computed(() => {
  const disableImageMap = {
    activity: walletBtnActivityDisable,
    cash: walletBtnCashDisable,
    point: walletBtnPointDisable,
  }
  return disableImageMap[props.mode] || walletBtnCashDisable
})

const getWalletNormalImage = computed(() => {
  const normalImageMap = {
    activity: walletBtnActivityNormal,
    cash: walletBtnCashNormal,
    point: walletBtnPointNormal,
  }
  return normalImageMap[props.mode] || walletBtnCashNormal
})

// Methods
const click = (event) => {
  $SoundControl.sound.play('click')
  emit('click', event)
}
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

.wallet-container {
  @apply from-header-field-from to-header-field-to bg-linear-to-b border-header-field-border;
  border-width: 0.0625rem;
  border-radius: 9999px;
  border-style: solid;
  width: 100%;
  height: 100%;
  padding: 2px;
}

.wallet-container.inner {
  @apply border-header-field-border/40;
  align-content: center;
  height: 100%;
}

.wallet-container.highlight {
  @apply border-header-field-border-highlight;
}

@media screen and (min-width: 880px) {
  .wallet-content {
    @apply flex-row mx-[2px];
  }
}

@media screen and (min-width: 1024px) {
  .wallet-content {
    @apply flex-row mx-[4px];
  }
}

.wallet-content-mode-icon {
  width: 12px;
  height: 12px;
}

@media screen and (min-width: 1024px) {
  .wallet-content-mode-icon {
    width: 24px;
    height: 24px;
  }
}

.wallet-content-label.highlight {
  @apply text-header-field-border-highlight;
}

.wallet-content-title {
  gap: 2px;
}

@media screen and (min-width: 880px) and (max-width: 1023px) {
  .wallet-content-title {
    gap: 1px;
  }
}

.wallet-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

@media screen and (min-width: 1024px) {
  .wallet-icon {
    width: 36px;
    height: 36px;
  }
}

.checked-icon {
  position: absolute;
  bottom: 0;
  inset-inline-end: 0;
  width: 14px;
  height: 14px;
}

@media screen and (min-width: 1024px) {
  .checked-icon {
    width: 18px;
    height: 18px;
  }
}
</style>
